import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Loader2, Plus } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Label, TextArea } from "@/components/ui/Input";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { HubUserSearchableMultiSelect } from "@/components/settings/HubUserSearchableMultiSelect";
import type { HubUserSelectOption } from "@/components/settings/hubUserSelectOptions";
import { useHubToast } from "@/components/ui/HubToast";
import { useIntegratorMe } from "@/lib/me-query";
import { EM_DASH, cn } from "@/lib/utils";

import {
  acknowledgeQualityContainmentNotice,
  addQualityContainmentRecipients,
  broadcastQualityContainmentNotice,
  fetchQualityContainmentAudience,
  resendQualityContainmentNotice,
  type ContainmentNotice,
  type QualityCaseDetailResponse,
} from "./casesApi";

function formatTimestamp(value: string | null): string {
  if (value == null || value.trim() === "") return EM_DASH;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function recipientLabel(displayName: string | null, email: string | null, userId: string): string {
  return displayName?.trim() || email?.trim() || userId;
}

function containmentSummary(notices: ContainmentNotice[]): { label: string; tone: "neutral" | "success" | "warning" } {
  if (notices.length === 0) {
    return { label: "No notices", tone: "neutral" };
  }
  const pendingCount = notices.reduce(
    (count, notice) => count + notice.recipients.filter((row) => row.acknowledgedAt == null).length,
    0,
  );
  if (pendingCount > 0) {
    return { label: `${String(pendingCount)} pending`, tone: "warning" };
  }
  const hasAudience = notices.some((notice) => notice.recipients.length > 0);
  return { label: hasAudience ? "All acknowledged" : "No contact", tone: hasAudience ? "success" : "neutral" };
}

export function QualityContainmentCard({
  caseId,
  notices,
  writesAllowed,
  onBroadcastSuccess,
}: {
  caseId: number;
  notices: ContainmentNotice[];
  writesAllowed: boolean;
  onBroadcastSuccess: (detail: QualityCaseDetailResponse) => Promise<void>;
}): JSX.Element {
  const { showToast } = useHubToast();
  const meQuery = useIntegratorMe();
  const currentUserId = meQuery.data?.user.id ?? null;
  const [body, setBody] = useState("");
  const [recipientUserIds, setRecipientUserIds] = useState<string[]>([]);
  const [expanded, setExpanded] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const summary = containmentSummary(notices);
  const panelId = `quality-case-${String(caseId)}-containment`;

  const audienceQuery = useQuery({
    queryKey: ["quality", "containment-audience"],
    queryFn: fetchQualityContainmentAudience,
    enabled: writesAllowed,
  });

  const audienceUsers: HubUserSelectOption[] = useMemo(
    () =>
      (audienceQuery.data?.users ?? []).map((user) => ({
        userId: user.id,
        email: user.email,
        displayName: user.display_name,
      })),
    [audienceQuery.data?.users],
  );

  const broadcastMutation = useMutation({
    mutationFn: () =>
      broadcastQualityContainmentNotice(caseId, {
        body: body.trim(),
        recipientUserIds,
      }),
    onSuccess: async (detail) => {
      setBody("");
      setRecipientUserIds([]);
      setComposerOpen(false);
      await onBroadcastSuccess(detail);
      showToast({ tone: "success", message: "Containment notice broadcast." });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const acknowledgeMutation = useMutation({
    mutationFn: (noticeId: number) => acknowledgeQualityContainmentNotice(caseId, noticeId),
    onSuccess: async (detail) => {
      await onBroadcastSuccess(detail);
      showToast({ tone: "success", message: "Containment acknowledged." });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  return (
    <Card>
      <CardHeader className={cn("flex flex-wrap items-center gap-2", !expanded ? "border-b-0" : undefined)}>
        <button
          type="button"
          className="-mx-1 flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-0.5 text-left hover:bg-slate-50"
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
          )}
          <CardTitle className="mb-0">Containment</CardTitle>
          {!expanded ? <Badge tone={summary.tone}>{summary.label}</Badge> : null}
        </button>
        <HoverHintWithGlyph hoverHint="Immediate shop-floor controls for this case, such as red-tagging a tester or a daily check. Broadcast sends an email and an in-app notification to the people who must sign off. Adding people later notifies only the new names. Resend reminds selected people or everyone still pending. This is not root-cause analysis and is not implied by a QC incident." />
        {writesAllowed && !composerOpen ? (
          <Button size="sm" variant="secondary" onClick={() => { setExpanded(true); setComposerOpen(true); }}>
            <Plus className="h-4 w-4" />
            New containment notice
          </Button>
        ) : null}
      </CardHeader>
      {expanded ? (
      <CardBody id={panelId} className="space-y-4">
        {writesAllowed && composerOpen ? (
          <div className="space-y-3 rounded-md border border-slate-200 p-3">
            <div>
              <Label htmlFor="containment-body">Notice</Label>
              <TextArea
                id="containment-body"
                rows={4}
                value={body}
                onChange={(event) => setBody(event.target.value)}
                placeholder="What is contained, how, and what people must not do until this is lifted"
              />
            </div>
            <HubUserSearchableMultiSelect
              id="containment-recipients"
              label="Acknowledge audience"
              users={audienceUsers}
              selectedUserIds={recipientUserIds}
              onSelectedUserIdsChange={setRecipientUserIds}
              placeholder="People who must sign off"
              noneSelectedLabel="No audience selected"
              addLabel="Add people"
            />
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="secondary"
                disabled={broadcastMutation.isPending}
                onClick={() => setComposerOpen(false)}
              >
                Cancel
              </Button>
              <Button
                disabled={
                  broadcastMutation.isPending || body.trim() === "" || recipientUserIds.length === 0
                }
                onClick={() => broadcastMutation.mutate()}
              >
                {broadcastMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Broadcast
              </Button>
            </div>
          </div>
        ) : null}

        {notices.length === 0 ? (
          <p className="text-sm text-slate-600">
            No containment notices yet. Broadcast one when people on the floor must stop using something or follow a check.
          </p>
        ) : (
          <div className="space-y-3">
            {notices.map((notice) => (
              <QualityContainmentNoticePanel
                key={notice.id}
                caseId={caseId}
                notice={notice}
                writesAllowed={writesAllowed}
                currentUserId={currentUserId}
                audienceUsers={audienceUsers}
                acknowledgePending={acknowledgeMutation.isPending}
                onAcknowledge={() => acknowledgeMutation.mutate(notice.id)}
                onUpdated={onBroadcastSuccess}
              />
            ))}
          </div>
        )}
      </CardBody>
      ) : null}
    </Card>
  );
}

function QualityContainmentNoticePanel({
  caseId,
  notice,
  writesAllowed,
  currentUserId,
  audienceUsers,
  acknowledgePending,
  onAcknowledge,
  onUpdated,
}: {
  caseId: number;
  notice: ContainmentNotice;
  writesAllowed: boolean;
  currentUserId: string | null;
  audienceUsers: HubUserSelectOption[];
  acknowledgePending: boolean;
  onAcknowledge: () => void;
  onUpdated: (detail: QualityCaseDetailResponse) => Promise<void>;
}): JSX.Element {
  const { showToast } = useHubToast();
  const [addUserIds, setAddUserIds] = useState<string[]>([]);
  const [selectedResendIds, setSelectedResendIds] = useState<string[]>([]);

  const pending = notice.recipients.filter((row) => row.acknowledgedAt == null);
  const currentRecipient = currentUserId
    ? notice.recipients.find((row) => row.userId.toLowerCase() === currentUserId.toLowerCase())
    : null;
  const canAcknowledge = currentRecipient != null && currentRecipient.acknowledgedAt == null;
  const alreadyOnNotice = useMemo(
    () => new Set(notice.recipients.map((row) => row.userId.toLowerCase())),
    [notice.recipients],
  );
  const addableUsers = audienceUsers.filter((user) => !alreadyOnNotice.has(user.userId.toLowerCase()));

  const addMutation = useMutation({
    mutationFn: () => addQualityContainmentRecipients(caseId, notice.id, addUserIds),
    onSuccess: async (detail) => {
      setAddUserIds([]);
      await onUpdated(detail);
      showToast({ tone: "success", message: "New people notified. Existing recipients were not resent." });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const resendMutation = useMutation({
    mutationFn: (input: { pendingOnly: true } | { recipientUserIds: string[] }) =>
      resendQualityContainmentNotice(caseId, notice.id, input),
    onSuccess: async (detail) => {
      setSelectedResendIds([]);
      await onUpdated(detail);
      showToast({ tone: "success", message: "Containment notice resent." });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const busy = addMutation.isPending || resendMutation.isPending;

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-slate-600">
          <Badge
            tone={
              notice.recipients.length === 0 ? "neutral" : pending.length === 0 ? "success" : "warning"
            }
          >
            {notice.recipients.length === 0
              ? "No contact"
              : pending.length === 0
                ? "All acknowledged"
                : `${String(pending.length)} pending`}
          </Badge>
          <span>
            {notice.recipients.length === 0 ? "Recorded" : "Broadcast"}{" "}
            {formatTimestamp(notice.broadcastAt)} by {notice.createdByDisplay ?? EM_DASH}
          </span>
        </div>
        {canAcknowledge ? (
          <Button size="sm" disabled={acknowledgePending} onClick={onAcknowledge}>
            {acknowledgePending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Acknowledge
          </Button>
        ) : null}
      </div>
      <p className="mt-2 whitespace-pre-wrap text-slate-800">{notice.body}</p>
      {notice.recipients.length > 0 ? (
        <ul className="mt-2 space-y-1 text-xs text-slate-600">
          {notice.recipients.map((row) => {
            const checked = selectedResendIds.includes(row.userId);
            return (
              <li key={row.userId} className="flex flex-wrap items-center gap-2">
                {writesAllowed ? (
                  <input
                    type="checkbox"
                    className="rounded border-slate-300"
                    checked={checked}
                    aria-label={`Select ${recipientLabel(row.displayName, row.email, row.userId)} for resend`}
                    onChange={() => {
                      setSelectedResendIds((current) =>
                        checked ? current.filter((id) => id !== row.userId) : [...current, row.userId],
                      );
                    }}
                  />
                ) : null}
                <span>
                  {recipientLabel(row.displayName, row.email, row.userId)}
                  {row.acknowledgedAt
                    ? ` · acknowledged ${formatTimestamp(row.acknowledgedAt)}`
                    : " · pending"}
                  {row.lastNotifiedAt ? ` · last sent ${formatTimestamp(row.lastNotifiedAt)}` : ""}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-slate-500">
          Historical notice copied from an investigation. Add people below to notify them.
        </p>
      )}
      {writesAllowed ? (
        <div className="mt-3 space-y-3 border-t border-slate-200 pt-3">
          <HubUserSearchableMultiSelect
            id={`containment-add-${String(notice.id)}`}
            label="Add people"
            users={addableUsers}
            selectedUserIds={addUserIds}
            onSelectedUserIdsChange={setAddUserIds}
            placeholder="People not already on this notice"
            noneSelectedLabel="No new people selected"
            addLabel="Add people"
          />
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={busy || pending.length === 0}
              onClick={() => resendMutation.mutate({ pendingOnly: true })}
            >
              {resendMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Resend to pending
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy || selectedResendIds.length === 0}
              onClick={() => resendMutation.mutate({ recipientUserIds: selectedResendIds })}
            >
              Resend to selected
            </Button>
            <Button
              size="sm"
              disabled={busy || addUserIds.length === 0}
              onClick={() => addMutation.mutate()}
            >
              {addMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Notify new people
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
